import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Users, Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CadreStrengthManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/pages/cadre-strength`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        // Initialize with empty data
        setData({
          id: 'cadre-1',
          header_title: 'Cadre Strength',
          header_subtitle: 'ICAR-CISH as on DD/MM/YYYY',
          intro_text: '',
          table_institute: [],
          table_rrs: [],
          table_technical: [],
          table_admin: [],
          footer_notes: ''
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await axios.post(`${API_URL}/api/pages/cadre-strength`, data, {
        headers: getAuthHeaders()
      });
      alert('Cadre Strength page updated successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      setError(error.response?.data?.detail || 'Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  // Institute table handlers
  const addInstituteRow = () => {
    setData({
      ...data,
      table_institute: [
        ...data.table_institute,
        {
          s_no: data.table_institute.length + 1,
          discipline: '',
          sanctioned: { sci: 0, sr_sci: 0, pr_sci: 0, hod: 0, total: 0 },
          in_position: { sci: 0, sr_sci: 0, pr_sci: 0, hod: 0, total: 0 }
        }
      ]
    });
  };

  const updateInstituteRow = (index, field, value) => {
    const updated = [...data.table_institute];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updated[index][parent][child] = parseInt(value) || 0;
      // Auto-calculate total
      if (child !== 'total') {
        const obj = updated[index][parent];
        obj.total = obj.sci + obj.sr_sci + obj.pr_sci + obj.hod;
      }
    } else {
      updated[index][field] = value;
    }
    setData({ ...data, table_institute: updated });
  };

  const deleteInstituteRow = (index) => {
    const updated = data.table_institute.filter((_, i) => i !== index);
    // Renumber s_no
    updated.forEach((row, i) => row.s_no = i + 1);
    setData({ ...data, table_institute: updated });
  };

  // RRS table handlers
  const addRRSRow = () => {
    setData({
      ...data,
      table_rrs: [
        ...data.table_rrs,
        {
          s_no: data.table_rrs.length + 1,
          discipline: '',
          sanctioned: { sci: 0, sr_sci: 0, pr_sci: 0, hod: 0, total: 0 },
          in_position: { sci: 0, sr_sci: 0, pr_sci: 0, hod: 0, total: 0 }
        }
      ]
    });
  };

  const updateRRSRow = (index, field, value) => {
    const updated = [...data.table_rrs];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updated[index][parent][child] = parseInt(value) || 0;
      if (child !== 'total') {
        const obj = updated[index][parent];
        obj.total = obj.sci + obj.sr_sci + obj.pr_sci + obj.hod;
      }
    } else {
      updated[index][field] = value;
    }
    setData({ ...data, table_rrs: updated });
  };

  const deleteRRSRow = (index) => {
    const updated = data.table_rrs.filter((_, i) => i !== index);
    updated.forEach((row, i) => row.s_no = i + 1);
    setData({ ...data, table_rrs: updated });
  };

  // Technical table handlers
  const addTechnicalRow = () => {
    setData({
      ...data,
      table_technical: [
        ...data.table_technical,
        { category: '', sanctioned_strength: 0, in_position: 0 }
      ]
    });
  };

  const updateTechnicalRow = (index, field, value) => {
    const updated = [...data.table_technical];
    updated[index][field] = field === 'category' ? value : (parseInt(value) || 0);
    setData({ ...data, table_technical: updated });
  };

  const deleteTechnicalRow = (index) => {
    setData({
      ...data,
      table_technical: data.table_technical.filter((_, i) => i !== index)
    });
  };

  // Admin table handlers
  const addAdminRow = () => {
    setData({
      ...data,
      table_admin: [
        ...data.table_admin,
        { post: '', sanctioned_strength: 0, in_position: 0 }
      ]
    });
  };

  const updateAdminRow = (index, field, value) => {
    const updated = [...data.table_admin];
    updated[index][field] = field === 'post' ? value : (parseInt(value) || 0);
    setData({ ...data, table_admin: updated });
  };

  const deleteAdminRow = (index) => {
    setData({
      ...data,
      table_admin: data.table_admin.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg text-red-600">Failed to load data</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-8 h-8 text-[#0E6D2B]" />
            Cadre Strength Management
          </h1>
          <p className="text-gray-600 mt-1">Manage staff position details</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Header Fields */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Page Header</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              value={data.header_title}
              onChange={(e) => setData({ ...data, header_title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle</label>
            <input
              type="text"
              value={data.header_subtitle}
              onChange={(e) => setData({ ...data, header_subtitle: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="ICAR-CISH as on DD/MM/YYYY"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Intro Text (HTML)</label>
            <Textarea
              value={data.intro_text}
              onChange={(e) => setData({ ...data, intro_text: e.target.value })}
              rows={4}
              placeholder="<p>Enter HTML content...</p>"
            />
            <p className="text-xs text-gray-500 mt-1">You can use HTML tags for formatting</p>
          </div>
        </div>
      </div>

      {/* Institute Cadre Table */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Institute Cadre Strength</h2>
          <Button onClick={addInstituteRow} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Add Row
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="border p-2">S.No</th>
                <th className="border p-2">Discipline</th>
                <th className="border p-2" colSpan="5">Sanctioned</th>
                <th className="border p-2" colSpan="5">In Position</th>
                <th className="border p-2">Actions</th>
              </tr>
              <tr>
                <th className="border p-2"></th>
                <th className="border p-2"></th>
                <th className="border p-2 text-xs">Sci</th>
                <th className="border p-2 text-xs">Sr.Sci</th>
                <th className="border p-2 text-xs">Pr.Sci</th>
                <th className="border p-2 text-xs">HOD</th>
                <th className="border p-2 text-xs">Total</th>
                <th className="border p-2 text-xs">Sci</th>
                <th className="border p-2 text-xs">Sr.Sci</th>
                <th className="border p-2 text-xs">Pr.Sci</th>
                <th className="border p-2 text-xs">HOD</th>
                <th className="border p-2 text-xs">Total</th>
                <th className="border p-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.table_institute.map((row, idx) => (
                <tr key={idx}>
                  <td className="border p-2 text-center">{row.s_no}</td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={row.discipline}
                      onChange={(e) => updateInstituteRow(idx, 'discipline', e.target.value)}
                      className="w-full px-2 py-1 border rounded"
                    />
                  </td>
                  <td className="border p-2">
                    <input type="number" value={row.sanctioned.sci} onChange={(e) => updateInstituteRow(idx, 'sanctioned.sci', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                  </td>
                  <td className="border p-2">
                    <input type="number" value={row.sanctioned.sr_sci} onChange={(e) => updateInstituteRow(idx, 'sanctioned.sr_sci', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                  </td>
                  <td className="border p-2">
                    <input type="number" value={row.sanctioned.pr_sci} onChange={(e) => updateInstituteRow(idx, 'sanctioned.pr_sci', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                  </td>
                  <td className="border p-2">
                    <input type="number" value={row.sanctioned.hod} onChange={(e) => updateInstituteRow(idx, 'sanctioned.hod', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                  </td>
                  <td className="border p-2 text-center font-bold">{row.sanctioned.total}</td>
                  <td className="border p-2">
                    <input type="number" value={row.in_position.sci} onChange={(e) => updateInstituteRow(idx, 'in_position.sci', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                  </td>
                  <td className="border p-2">
                    <input type="number" value={row.in_position.sr_sci} onChange={(e) => updateInstituteRow(idx, 'in_position.sr_sci', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                  </td>
                  <td className="border p-2">
                    <input type="number" value={row.in_position.pr_sci} onChange={(e) => updateInstituteRow(idx, 'in_position.pr_sci', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                  </td>
                  <td className="border p-2">
                    <input type="number" value={row.in_position.hod} onChange={(e) => updateInstituteRow(idx, 'in_position.hod', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                  </td>
                  <td className="border p-2 text-center font-bold">{row.in_position.total}</td>
                  <td className="border p-2 text-center">
                    <button onClick={() => deleteInstituteRow(idx)} className="text-red-600 hover:text-red-800">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* RRS Table - Similar structure */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">RRS Malda</h2>
          <Button onClick={addRRSRow} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Add Row
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="border p-2">S.No</th>
                <th className="border p-2">Discipline</th>
                <th className="border p-2" colSpan="5">Sanctioned</th>
                <th className="border p-2" colSpan="5">In Position</th>
                <th className="border p-2">Actions</th>
              </tr>
              <tr>
                <th className="border p-2"></th>
                <th className="border p-2"></th>
                <th className="border p-2 text-xs">Sci</th>
                <th className="border p-2 text-xs">Sr.Sci</th>
                <th className="border p-2 text-xs">Pr.Sci</th>
                <th className="border p-2 text-xs">HOD</th>
                <th className="border p-2 text-xs">Total</th>
                <th className="border p-2 text-xs">Sci</th>
                <th className="border p-2 text-xs">Sr.Sci</th>
                <th className="border p-2 text-xs">Pr.Sci</th>
                <th className="border p-2 text-xs">HOD</th>
                <th className="border p-2 text-xs">Total</th>
                <th className="border p-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.table_rrs.map((row, idx) => (
                <tr key={idx}>
                  <td className="border p-2 text-center">{row.s_no}</td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={row.discipline}
                      onChange={(e) => updateRRSRow(idx, 'discipline', e.target.value)}
                      className="w-full px-2 py-1 border rounded"
                    />
                  </td>
                  <td className="border p-2"><input type="number" value={row.sanctioned.sci} onChange={(e) => updateRRSRow(idx, 'sanctioned.sci', e.target.value)} className="w-16 px-2 py-1 border rounded" /></td>
                  <td className="border p-2"><input type="number" value={row.sanctioned.sr_sci} onChange={(e) => updateRRSRow(idx, 'sanctioned.sr_sci', e.target.value)} className="w-16 px-2 py-1 border rounded" /></td>
                  <td className="border p-2"><input type="number" value={row.sanctioned.pr_sci} onChange={(e) => updateRRSRow(idx, 'sanctioned.pr_sci', e.target.value)} className="w-16 px-2 py-1 border rounded" /></td>
                  <td className="border p-2"><input type="number" value={row.sanctioned.hod} onChange={(e) => updateRRSRow(idx, 'sanctioned.hod', e.target.value)} className="w-16 px-2 py-1 border rounded" /></td>
                  <td className="border p-2 text-center font-bold">{row.sanctioned.total}</td>
                  <td className="border p-2"><input type="number" value={row.in_position.sci} onChange={(e) => updateRRSRow(idx, 'in_position.sci', e.target.value)} className="w-16 px-2 py-1 border rounded" /></td>
                  <td className="border p-2"><input type="number" value={row.in_position.sr_sci} onChange={(e) => updateRRSRow(idx, 'in_position.sr_sci', e.target.value)} className="w-16 px-2 py-1 border rounded" /></td>
                  <td className="border p-2"><input type="number" value={row.in_position.pr_sci} onChange={(e) => updateRRSRow(idx, 'in_position.pr_sci', e.target.value)} className="w-16 px-2 py-1 border rounded" /></td>
                  <td className="border p-2"><input type="number" value={row.in_position.hod} onChange={(e) => updateRRSRow(idx, 'in_position.hod', e.target.value)} className="w-16 px-2 py-1 border rounded" /></td>
                  <td className="border p-2 text-center font-bold">{row.in_position.total}</td>
                  <td className="border p-2 text-center">
                    <button onClick={() => deleteRRSRow(idx)} className="text-red-600 hover:text-red-800">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Technical Staff Table */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Technical Staff Summary</h2>
          <Button onClick={addTechnicalRow} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Add Row
          </Button>
        </div>
        <table className="w-full border-collapse">
          <thead className="bg-gray-50">
            <tr>
              <th className="border p-2">Category</th>
              <th className="border p-2">Sanctioned Strength</th>
              <th className="border p-2">In Position</th>
              <th className="border p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.table_technical.map((row, idx) => (
              <tr key={idx}>
                <td className="border p-2">
                  <input
                    type="text"
                    value={row.category}
                    onChange={(e) => updateTechnicalRow(idx, 'category', e.target.value)}
                    className="w-full px-2 py-1 border rounded"
                  />
                </td>
                <td className="border p-2">
                  <input
                    type="number"
                    value={row.sanctioned_strength}
                    onChange={(e) => updateTechnicalRow(idx, 'sanctioned_strength', e.target.value)}
                    className="w-full px-2 py-1 border rounded"
                  />
                </td>
                <td className="border p-2">
                  <input
                    type="number"
                    value={row.in_position}
                    onChange={(e) => updateTechnicalRow(idx, 'in_position', e.target.value)}
                    className="w-full px-2 py-1 border rounded"
                  />
                </td>
                <td className="border p-2 text-center">
                  <button onClick={() => deleteTechnicalRow(idx)} className="text-red-600 hover:text-red-800">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Administrative Staff Table */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Administrative Staff Summary</h2>
          <Button onClick={addAdminRow} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Add Row
          </Button>
        </div>
        <table className="w-full border-collapse">
          <thead className="bg-gray-50">
            <tr>
              <th className="border p-2">Post</th>
              <th className="border p-2">Sanctioned Strength</th>
              <th className="border p-2">In Position</th>
              <th className="border p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.table_admin.map((row, idx) => (
              <tr key={idx}>
                <td className="border p-2">
                  <input
                    type="text"
                    value={row.post}
                    onChange={(e) => updateAdminRow(idx, 'post', e.target.value)}
                    className="w-full px-2 py-1 border rounded"
                  />
                </td>
                <td className="border p-2">
                  <input
                    type="number"
                    value={row.sanctioned_strength}
                    onChange={(e) => updateAdminRow(idx, 'sanctioned_strength', e.target.value)}
                    className="w-full px-2 py-1 border rounded"
                  />
                </td>
                <td className="border p-2">
                  <input
                    type="number"
                    value={row.in_position}
                    onChange={(e) => updateAdminRow(idx, 'in_position', e.target.value)}
                    className="w-full px-2 py-1 border rounded"
                  />
                </td>
                <td className="border p-2 text-center">
                  <button onClick={() => deleteAdminRow(idx)} className="text-red-600 hover:text-red-800">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer Notes */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Footer Notes</h2>
        <Textarea
          value={data.footer_notes}
          onChange={(e) => setData({ ...data, footer_notes: e.target.value })}
          rows={4}
          placeholder="<p>Enter HTML content for footer notes...</p>"
        />
        <p className="text-xs text-gray-500 mt-1">You can use HTML tags for formatting</p>
      </div>

      {/* Save Button at Bottom */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
    </AdminLayout>
  );
}
